import { Component } from '@verndale/core';
import { nanoid } from 'nanoid';

import keys from '../utils/keys';
import breakpoints from '../utils/breakpoints';

class Footer extends Component {
  setupDefaults() {
    this.dom = {
      $accordion: this.el._$('.footer__accordion'),
      $sectionHeadings: this.el._$$('.footer__section-heading')
    };

    this.currentViewport = null;
    this.handleViewport();
  }

  addListeners() {
    const { $accordion } = this.dom;

    $accordion._on('click', ::this.handleClick);
    $accordion._on('keydown', ::this.handleKeydown);
    breakpoints.addBreakpointListenerLg(::this.handleViewport);
  }

  handleClick(e) {
    const $button = e.target.closest('.footer__section-heading-button');

    if ($button) {
      const $heading = $button.parentNode;
      const isOpen = $button.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        $heading.classList.add('footer__section-heading--closed');
        $button.setAttribute('aria-expanded', 'false');
      } else {
        $heading.classList.remove('footer__section-heading--closed');
        $button.setAttribute('aria-expanded', 'true');
      }
    }
  }

  handleKeydown(e) {
    const { activeElement } = document;
    const isButtonActive = activeElement.matches(
      '.footer__section-heading-button'
    );
    const $buttons = this.el._$$('.footer__section-heading-button');
    const currentButtonIndex = () =>
      $buttons.findIndex((button) => button === activeElement);
    const { length } = $buttons;

    switch (e.keyCode) {
      case keys.home:
        e.preventDefault();
        $buttons[0].focus();
        break;
      case keys.end:
        e.preventDefault();
        $buttons[length - 1].focus();
        break;
      case keys.up:
        e.preventDefault();
        if (isButtonActive) {
          $buttons[(currentButtonIndex() - 1 + length) % length].focus();
        }
        break;
      case keys.down:
        e.preventDefault();
        if (isButtonActive) {
          $buttons[(currentButtonIndex() + 1) % length].focus();
        }
        break;
      default:
        break;
    }
  }

  handleViewport() {
    if (breakpoints.isLg()) {
      this.setLgViewportDom();
      this.currentViewport = 'lg';
    } else {
      this.setXsViewportDom();
      this.currentViewport = 'xs';
    }
  }

  setLgViewportDom() {
    const { $sectionHeadings } = this.dom;

    if (!this.currentViewport) {
      $sectionHeadings.forEach((heading) => {
        const $sectionContent = heading.nextElementSibling;
        const $wrapper = this.createSectionWrapper();

        heading.parentNode.insertBefore($wrapper, heading);
        this.appendSectionWrapperChilds($wrapper, heading, $sectionContent);
      });
    } else if (this.currentViewport === 'xs') {
      $sectionHeadings.forEach((heading) => {
        const $button = heading._$('.footer__section-heading-button');
        const $title = heading._$('span');
        const $sectionContent = heading.nextElementSibling;
        const $wrapper = this.createSectionWrapper();

        heading.appendChild($title);
        heading.removeChild($button);
        ['role', 'aria-labelledby', 'id'].forEach((attr) =>
          $sectionContent.removeAttribute(attr)
        );
        heading.parentNode.insertBefore($wrapper, heading);
        this.appendSectionWrapperChilds($wrapper, heading, $sectionContent);
      });
    }
  }

  setXsViewportDom() {
    const { $sectionHeadings, $accordion } = this.dom;

    if (!this.currentViewport) {
      $sectionHeadings.forEach((heading) => {
        const sectionContentId = `id-${nanoid()}`;
        const buttonId = `id-${nanoid()}`;
        const $sectionContent = heading.nextElementSibling;
        const $button = this.createSectionButton(heading);
        const $title = heading._$('span');

        this.setSectionButtonAttrs($button, sectionContentId, buttonId);
        heading.appendChild($button);
        $button.appendChild($title);
        this.setSectionContentAttrs(
          $sectionContent,
          sectionContentId,
          buttonId
        );
      });
    } else if (this.currentViewport === 'lg') {
      $sectionHeadings.forEach((heading) => {
        const sectionContentId = `id-${nanoid()}`;
        const buttonId = `id-${nanoid()}`;
        const $sectionContent = heading.nextElementSibling;
        const $button = this.createSectionButton(heading);
        const $title = heading._$('span');
        const $wrapper = heading.parentNode;

        this.setSectionButtonAttrs($button, sectionContentId, buttonId);
        $accordion.appendChild(heading);
        $accordion.appendChild($sectionContent);
        $accordion.removeChild($wrapper);
        heading.appendChild($button);
        $button.appendChild($title);
        this.setSectionContentAttrs(
          $sectionContent,
          sectionContentId,
          buttonId
        );
      });
    }
  }

  createSectionButton($heading) {
    const $button = document.createElement('button');
    $button.classList.add('footer__section-heading-button');
    $heading.classList.add('footer__section-heading--closed');

    return $button;
  }

  createSectionWrapper() {
    const $wrapper = document.createElement('div');
    $wrapper.classList.add('footer__section');

    return $wrapper;
  }

  appendSectionWrapperChilds($wrapper, $heading, $sectionContent) {
    $wrapper.appendChild($heading);
    $wrapper.appendChild($sectionContent);
  }

  setSectionContentAttrs($sectionContent, sectionContentId, buttonId) {
    $sectionContent.id = sectionContentId;
    $sectionContent.setAttribute('role', 'region');
    $sectionContent.setAttribute('aria-labelledby', buttonId);
  }

  setSectionButtonAttrs($button, sectionContentId, buttonId) {
    $button.setAttribute('aria-expanded', 'false');
    $button.setAttribute('aria-controls', sectionContentId);
    $button.id = buttonId;
  }
}

export default Footer;
